<template>
  <v-container v-if="isLoaded">
    <v-form ref="search-entity-event-task-form">
      <v-card class="mt-2">
        <base-card-title
          icon="mdi-calendar-clock"
          label="Search entity event tasks"
        />
        <v-card-text class="pl-8 pr-8">
          <v-row>
            <v-col
              class="pb-0 pt-0"
              md="3"
            >
              <base-input-select
                :items="entityTypes"
                item-label="Entity type"
                item-value="name"
                item-text="description"
                :selected-value="entityEventTaskSearchCriteria.entityType"
                @change="entityEventTaskSearchCriteria.entityType = arguments[0]"
              />
            </v-col>
            <v-col
              class="pb-0 pt-0"
              md="3"
            >
              <base-input-select
                :items="eventStatuses"
                item-label="Status"
                item-value="name"
                item-text="name"
                :selected-value="entityEventTaskSearchCriteria.status"
                @change="entityEventTaskSearchCriteria.status = arguments[0]"
              />
            </v-col>
            <v-col
              class="pb-0 pt-0"
              md="3"
            >
              <base-input-text-field
                item-label="Entity event id"
                :value="entityEventTaskSearchCriteria.entityEventId"
                @blur="entityEventTaskSearchCriteria.entityEventId = arguments[0]"
              />
            </v-col>
            <v-col
              class="pb-0 pt-0"
              md="3"
            >
              <base-input-select
                :items="eventSources"
                item-label="Event source"
                item-value="name"
                item-text="name"
                :selected-value="entityEventTaskSearchCriteria.source"
                @change="entityEventTaskSearchCriteria.source = arguments[0]"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col
              class="pb-0 pt-0"
              md="3"
            >
              <base-input-text-field
                item-label="Entity instance id"
                :value="entityEventTaskSearchCriteria.entityInstanceId"
                @blur="entityEventTaskSearchCriteria.entityInstanceId = arguments[0]"
              />
            </v-col>
            <v-col
              class="pb-0 pt-0"
              md="3"
            >
              <base-input-text-field
                item-label="Target processor id"
                :value="entityEventTaskSearchCriteria.targetProcessorId"
                @blur="entityEventTaskSearchCriteria.targetProcessorId = arguments[0]"
              />
            </v-col>
            <v-col
              class="pb-0 pt-0"
              md="3"
            >
              <base-input-select-date-picker
                item-label="From event time"
                :selected-value="entityEventTaskSearchCriteria.fromEventTime"
                @change="entityEventTaskSearchCriteria.fromEventTime = arguments[0]"
              />
            </v-col>
            <v-col
              class="pb-0 pt-0"
              md="3"
            >
              <base-input-select-date-picker
                item-label="To event time"
                :rules="[rules.fromDateIsBeforeToDate]"
                :selected-value="entityEventTaskSearchCriteria.toEventTime"
                @change="entityEventTaskSearchCriteria.toEventTime = arguments[0]"
              />
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-form>

    <div class="mt-2">
      <base-input-primary-button
        icon="mdi-magnify"
        label="Search"
        @click="search"
      />
      <base-input-secondary-button
        icon="mdi-close"
        label="Reset"
        @click="resetCriteria"
      />
      <base-input-primary-button
        v-if="showBulkRetryOption"
        class="ml-2"
        icon="mdi-refresh"
        label="Bulk retry"
        @click="doBulkRetry"
      />
    </div>

    <v-card class="mt-10">
      <base-table-paged-server-side
        id="entityEventTaskResultsTable"
        ref="table"
        :search-criteria="entityEventTaskSearchCriteria"
        :table-headers="tableHeaders"
        search-result-name="entityEventTaskSearchResults"
        url="entity-event-task/criteria-search"
        @toggleDownloadLinkDisplay="showBulkRetry"
      >
        <template #item.eventTime="{ item }">
          {{ item.eventTime | formatDateTimeFromInstant }}
        </template>
        <template #item.processingStartTime="{ item }">
          {{ item.processingStartTime | formatDateTimeFromInstant }}
        </template>

        <template #item.status="{ item }">
          <base-tool-tip
            :label="getStatus(item.status).title"
            :icon="getStatus(item.status).image"
            :icon-color="getStatus(item.status).color"
          />
        </template>

        <template #item.action="{ item }">
          <base-tool-tip
            label="View detail"
            icon="mdi-magnify"
            @click="showViewDialog(item)"
          />
          <base-tool-tip
            v-if="!isCompleted(item)"
            label="Retry"
            icon="mdi-refresh"
            icon-color="blue"
            @click="retry(item)"
          />
          <base-tool-tip
            v-if="isCompleted(item)"
            label="Completed"
            icon-color="green"
            icon="mdi-check-circle"
          />
        </template>
      </base-table-paged-server-side>
    </v-card>

    <view-entity-event-task-details-dialog
      :view-dialog="showViewDetailDialog"
      :entity-event-task-detail="entityEventTaskDetail"
      @close="() => showViewDetailDialog = false"
    />

    <v-dialog
      v-model="showRetryDialog"
      :persistent="true"
      max-width="30%"
    >
      <v-card class="mt-2">
        <base-card-title label="RETRY RESPONSE" />
        <v-card-text>
          <div>
            {{ responseStatusMessage }}
          </div>
          <div v-if="message">
            <br>
            <b>Message:</b> {{ message }}
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <base-input-primary-button
            label="Close"
            @click="close"
          />
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import axios from 'axios'
import BaseCardTitle from '@/base/card/BaseCardTitle'
import { mapGetters } from 'vuex'
import BaseTablePagedServerSide from '@/base/table/BaseTablePagedServerSide.vue'
import BaseInputPrimaryButton from '@/base/input/BaseInputPrimaryButton.vue'
import BaseInputSecondaryButton from '@/base/input/BaseInputSecondaryButton.vue'
import { validateDate } from '@/validation/customvalidation'
import BaseInputSelectDatePicker from '@/base/input/BaseInputSelectDatePicker.vue'
import BaseToolTip from '@/base/tooltip/BaseToolTip.vue'
import ViewEntityEventTaskDetailsDialog from '@/views/event/ViewEntityEventTaskDetailsDialog.vue'
import { entityEventStatusMixin } from '@/mixins/entityEventStatusMixin'
import BaseInputSelect from '@/base/input/BaseInputSelect.vue'
import BaseInputTextField from '@/base/input/BaseInputTextField.vue'

export default {
  name: 'SearchEntityEventTasks',
  components: {
    BaseInputTextField,
    BaseInputSelect,
    BaseToolTip,
    BaseCardTitle,
    BaseTablePagedServerSide,
    BaseInputPrimaryButton,
    BaseInputSecondaryButton,
    BaseInputSelectDatePicker,
    ViewEntityEventTaskDetailsDialog
  },
  mixins: [entityEventStatusMixin],
  data () {
    return {
      isLoaded: false,
      showViewDetailDialog: false,
      showRetryDialog: false,
      showBulkRetryOption: false,
      entityTypes: [],
      eventStatuses: [],
      eventSources: [],
      selectedEventTaskIds: [],
      entityEventTaskDetail: null,
      responseStatusMessage: '',
      message: '',
      tableHeaders: [
        { text: 'Entity type', align: 'start', sortable: false, value: 'entityType.name', width: '3%' },
        { text: 'Source', align: 'start', sortable: false, value: 'source', width: '3%' },
        { text: 'Entity event id', align: 'start', sortable: false, value: 'entityEventId', width: '10%' },
        { text: 'Entity instance id', align: 'start', sortable: false, value: 'entityInstanceId' },
        { text: 'Target processor id', align: 'start', sortable: false, value: 'targetProcessorId' },
        { text: 'Event time', align: 'start', sortable: false, value: 'eventTime' },
        { text: 'Processing start time', align: 'start', sortable: false, value: 'processingStartTime' },
        { text: 'Status', align: 'start', sortable: false, value: 'status', width: '3%' },
        { text: 'Fail count', align: 'start', sortable: false, value: 'failCount', width: '2%' },
        { text: 'Actions', align: 'start', sortable: false, value: 'action', width: '6%' }
      ],
      rules: {
        fromDateIsBeforeToDate: value => validateDate(this.entityEventTaskSearchCriteria.fromEventTime, value) || 'To event time cannot be before from event time.'
      }
    }
  },
  computed: {
    ...mapGetters([
      'entityEventTaskSearchCriteria'
    ])
  },
  async mounted () {
    await axios.all([
      axios.get('/entity-event-task/statuses')
        .then(response => {
          this.eventStatuses.push(...response.data)
        })
        .catch(e => console.log(e)),

      axios.get('/entity-event-task/sources')
        .then(response => {
          this.eventSources.push(...response.data)
        })
        .catch(e => console.log(e)),

      axios.get('/entity-event-task/entityTypes')
        .then(response => {
          this.entityTypes.push(...response.data)
        })
        .catch(e => console.log(e))
    ]).then(() => {
      this.isLoaded = true
    })
  },
  methods: {
    validate () {
      return this.$refs['search-entity-event-task-form'].validate()
    },
    isCompleted (item) {
      return item.status === 'COMPLETED'
    },
    showBulkRetry (hasResult) {
      this.showBulkRetryOption = !!hasResult
    },
    search () {
      if (this.validate()) {
        this.$refs.table.doSearch()
      }
    },
    resetCriteria () {
      this.$refs.table.doReset()
      this.$store.commit('resetEntityEventTaskSearchCriteria')
    },
    showViewDialog (item) {
      this.entityEventTaskDetail = item
      this.showViewDetailDialog = true
    },
    retry (item) {
      this.message = ''
      this.responseStatusMessage = ''
      axios.put('/entity-event-task/retry/' + item.id)
        .then((response) => {
          if (response.data.status === 'FAILED') {
            this.responseStatusMessage = 'Retry task execution failed'
            this.message = response.data.message
          } else {
            this.responseStatusMessage = 'Retry task execution successful'
            this.search()
          }
          this.showRetryDialog = true
        })
        .catch(e => console.log(e))
    },
    doBulkRetry () {
      this.message = ''
      this.responseStatusMessage = ''
      this.selectedEventTaskIds = []
      this.$refs.table.items
        .filter(item => item.status !== 'COMPLETED')
        .forEach((data) => {
          this.selectedEventTaskIds.push(data.id)
        })
      axios.put('/entity-event-task/bulk-retry', this.selectedEventTaskIds)
        .then((response) => {
          const results = response.data

          if (Array.isArray(results)) {
            const failedResults = results.filter(result => result && result.status === 'FAILED')

            if (failedResults.length > 0) {
              this.responseStatusMessage = 'Bulk retry task execution failed'
              this.message = failedResults
                .map(result => result && result.message)
                .filter(message => !!message)
                .join('; ')
            } else {
              this.responseStatusMessage = 'Bulk retry task execution successful'
              this.search()
            }
          } else {
            if (results && results.status === 'FAILED') {
              this.responseStatusMessage = 'Bulk retry task execution failed'
              this.message = results.message
            } else {
              this.responseStatusMessage = 'Bulk retry task execution successful'
              this.search()
            }
          }
          this.showRetryDialog = true
        })
        .catch(e => console.log(e))
    },
    close () {
      this.showRetryDialog = false
      this.search()
    }
  }
}
</script>

<style scoped>
.container{
  max-width: 100%;
}
::v-deep .v-data-table th:first-child,
.v-data-table td:first-child {
  width: 10px !important;
}
</style>
